/**
 * LinkedPipelinePanel — parents deferred until their migrated linked children are published on
 * the target. Ported from the linked-process-migration prototype; now shown inside the merged
 * Process Migration flow (form step + linked done screen).
 *
 * Must be rendered inside a `.leu-scope` wrapper (see linked-process-migration.css).
 */
import React from "react";
import "./linked-process-migration.css";

import {
  useLinkedChecklistPipeline,
  useLinkedChecklistPipelineContinue,
  useLinkedChecklistPipelineCancel,
  type LinkedMigrationPipelineEntry,
} from "../../../lib/api-central-mu";
import { Check, Alert, Refresh, Clock, Trash, Package, ArrowRight } from "./icons";

const LinkedPipelinePanel: React.FC<{
  hideWhenEmpty?: boolean;
  /** Fires with the updated entry after a "Continue migration" — lets the parent view reflect the result. */
  onEntryResolved?: (entry: LinkedMigrationPipelineEntry) => void;
}> = ({ hideWhenEmpty, onEntryResolved }) => {
  const pipelineQuery = useLinkedChecklistPipeline();
  const continueMut = useLinkedChecklistPipelineContinue();
  const cancelMut = useLinkedChecklistPipelineCancel();
  const entries = pipelineQuery.data ?? [];

  if (hideWhenEmpty && !pipelineQuery.isLoading && entries.length === 0) return null;

  const statusBadge = (e: LinkedMigrationPipelineEntry) => {
    if (e.status === "READY") return <span className="leu-badge leu-badge--success" style={{ gap: 6 }}><Check size={12} stroke={3} />Ready to migrate</span>;
    if (e.status === "MIGRATED") return <span className="leu-badge leu-badge--success" style={{ gap: 6 }}><Check size={12} stroke={3} />Migrated</span>;
    if (e.status === "FAILED") return <span className="leu-badge" style={{ gap: 6, background: "color-mix(in oklch, var(--destructive) 14%, var(--card))", color: "var(--destructive)" }}><Alert size={11} />Failed</span>;
    return <span className="leu-badge leu-badge--warning" style={{ gap: 6 }}><Clock size={11} />Waiting for publish</span>;
  };

  return (
    <div className="leu-card" style={{ overflow: "hidden", marginBottom: 20 }}>
      <div style={{ padding: "13px 18px", borderBottom: "1px solid var(--border)", background: "var(--bg-2)", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--fg-3)", fontWeight: 500 }}>Migration pipeline</span>
        <span style={{ fontSize: 12, color: "var(--fg-3)" }}>parents waiting for linked children to be published</span>
        <div style={{ flex: 1 }} />
        <button className="leu-btn leu-btn--outline leu-btn--sm" onClick={() => pipelineQuery.refetch()} disabled={pipelineQuery.isFetching} style={pipelineQuery.isFetching ? { opacity: 0.5 } : undefined}><Refresh size={13} />Re-check publish status</button>
      </div>

      {pipelineQuery.isLoading && (<div style={{ padding: 24, textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>Loading pipeline…</div>)}
      {!pipelineQuery.isLoading && entries.length === 0 && (<div style={{ padding: 24, textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>No deferred migrations. Parents whose linked children still need publishing will appear here.</div>)}

      {entries.map((e) => {
        const migrateChildren = e.children.filter((c) => c.action === "MIGRATE");
        const allPublished = migrateChildren.every((c) => c.published);
        return (
          <div key={e.id} style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ width: 28, height: 28, borderRadius: 7, background: "var(--bg-3)", color: "var(--fg-3)", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Package size={16} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{e.parentChecklistName ?? e.parentChecklistId}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>{e.parentChecklistId}</div>
              </div>
              {statusBadge(e)}
            </div>

            {migrateChildren.length > 0 && (
              <div style={{ marginTop: 10, marginLeft: 40, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{allPublished ? "All linked children are published on the target." : "Publish these linked children on the target, then re-check:"}</div>
                {migrateChildren.map((c) => (
                  <div key={c.sourceId} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                    {c.published
                      ? <span style={{ display: "inline-flex", color: "var(--success)" }}><Check size={13} stroke={3} /></span>
                      : <span style={{ display: "inline-flex", color: "var(--fg-4)" }}><Clock size={13} /></span>}
                    <span style={{ color: c.published ? "var(--fg-2)" : "var(--fg-3)" }}>{c.name ?? c.sourceId}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg-4)" }}>{c.sourceId}</span>
                    {c.facilityId && <span style={{ fontSize: 10.5, color: "var(--fg-4)" }}>· facility {c.facilityId}</span>}
                  </div>
                ))}
              </div>
            )}

            {e.status === "FAILED" && e.statusMessage && (
              <div style={{ marginTop: 10, marginLeft: 40, fontSize: 12, color: "var(--destructive)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{e.statusMessage}</div>
            )}

            {e.status !== "MIGRATED" && (
              <div style={{ marginTop: 12, marginLeft: 40, display: "flex", gap: 8 }}>
                <button className="leu-btn leu-btn--primary leu-btn--sm" disabled={!allPublished || continueMut.isPending} style={(!allPublished || continueMut.isPending) ? { opacity: 0.45, pointerEvents: "none" } : undefined} onClick={() => continueMut.mutate(e.id, { onSuccess: (entry) => onEntryResolved?.(entry) })}><ArrowRight size={13} />{e.status === "FAILED" ? "Retry import" : "Continue migration"}</button>
                {/* The linked children are already migrated on the target once a parent is deferred here,
                    so "Remove" is offered ONLY to abandon a FAILED deferral — never during the normal
                    waiting/ready flow, where the user should publish the children and Continue. */}
                {e.status === "FAILED" && (
                  <button className="leu-btn leu-btn--outline leu-btn--sm" disabled={cancelMut.isPending} onClick={() => cancelMut.mutate(e.id)}><Trash size={13} />Remove</button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LinkedPipelinePanel;
